/**
 * File Size Validation Utility
 * Provides file size validation and error messages
 */

// Maximum file size: 10MB (matching Firebase Storage rules)
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
// Maximum video file size: 100MB
export const MAX_VIDEO_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Validate file size before upload
 * @throws Error with clear message if file is too large
 */
export function validateFileSize(file: File, maxSize: number = MAX_FILE_SIZE): void {
  if (file.size > maxSize) {
    const fileSizeFormatted = formatFileSize(file.size);
    const maxSizeFormatted = formatFileSize(maxSize);
    throw new Error(
      `File "${file.name}" is too large (${fileSizeFormatted}). Maximum file size is ${maxSizeFormatted}. Please compress the file or choose a smaller file.`
    );
  }
}

/**
 * Check if file size is valid (returns boolean instead of throwing)
 */
export function isFileSizeValid(file: File, maxSize: number = MAX_FILE_SIZE): boolean {
  return file.size <= maxSize;
}

/**
 * Get maximum file size based on file type
 * Videos can be up to 100MB, other files up to 10MB
 */
export function getMaxFileSize(file: File): number {
  if (file.type.startsWith('video/')) {
    return MAX_VIDEO_FILE_SIZE;
  }
  return MAX_FILE_SIZE;
}

/**
 * Validate file size based on file type (videos can be larger)
 */
export function validateFileSizeByType(file: File): void {
  const maxSize = getMaxFileSize(file);
  validateFileSize(file, maxSize);
}

